import React, { useState, useEffect } from 'react';

import { Drawer, Modal } from 'antd';

import styled from 'styled-components';

//import sharedEmitter from '@libs/EventEmitter';

import { useLayoutContext, useLayoutContextDispatch } from '@contexts/LayoutContext';

import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const { confirm } = Modal;

const StyledDrawer = styled(Drawer)`
    .ant-drawer-content-wrapper {
        min-width: ${(props) => props.$minWidth};
        max-width: ${(props) => props.$maxWidth};

        @media (max-width: 768px) {
            min-width: 100vw;
        }

        .ant-drawer-content {
            background: ${(props) => props.$customBgColor};

            .ant-drawer-wrapper-body {
                .ant-drawer-header {
                    background-color: ${(props) => (props.$modalDrawer ? '#000' : 'inherit')};
                    border-bottom: ${(props) => (props.$modalDrawer ? 'none' : '1px solid #f0f0f0')};
                    border-radius: 0;

                    .ant-drawer-close {
                        display: none;
                    }
                    .ant-drawer-title {
                        padding-left: ${(props) => (props.$fixed || props.$left ? '0' : '24px')};
                        color: ${(props) => (props.$modalDrawer ? '#fff' : props.theme.TextColor)};
                    }

                    &.ant-drawer-header-sticky {
                        position: sticky;
                        z-index: 5;
                        background: white;
                        top: 0;
                        overflow: hidden;
                    }
                    &.ant-drawer-header-noborder {
                        border-bottom: none;
                    }
                }

                .ant-drawer-body {
                    background-color: ${(props) =>
                        props.$customBgColor
                            ? props.$customBgColor
                            : props.$whiteBg
                              ? '#fff'
                              : props.theme.LayoutBodyBackground};
                    padding: ${(props) => props.$padding};

                    .drawer-toolbar-container {
                        display: flex;
                        justify-content: flex-end;
                        align-items: flex-start;
                        flex-wrap: wrap;
                        padding: 0.5rem 0 1rem 0;
                    }

                    .drawer-content {
                        .ant-card {
                            margin: 1em 0;
                            .card-header {
                                margin: 0 16px !important;
                                .title {
                                    font-size: 1.2rem !important;
                                    font-weight: bold !important;
                                }
                                .label {
                                    font-size: 0.8rem !important;
                                }
                            }

                            .ant-card-head {
                                height: unset;
                                overflow: unset;
                                flex: unset;
                            }

                            .ant-card-body-content {
                                .base-item {
                                    ul {
                                        margin: 1em 0 0 0;
                                        padding: 0;
                                        list-style: none;

                                        li {
                                            margin: 0.25em 0;
                                        }
                                    }
                                }
                            }
                        }

                        .ant-ribbon {
                            top: -8px;
                            text-transform: uppercase;
                            font-size: 0.7em;
                            vertical-align: middle;
                            line-height: 2.6em;

                            svg {
                                margin-left: 0.25em;
                                font-size: 1.5em;
                            }
                        }
                    }

                    &.standard {
                        .ant-card {
                            border-color: #d9d9d9;
                            .ant-card-head {
                                border-color: #d9d9d9;
                            }
                        }
                    }
                }
            }
        }
    }

    &.no-title {
        .ant-drawer-header-no-title {
            display: none;
        }
    }

    &.max-width-800 {
        .ant-drawer-content-wrapper {
            max-width: 800px;
        }
    }
`;

const DrawerCloseButton = styled.span`
    position: absolute;
    top: 0.4em;
    left: ${(props) => (props.$placement !== 'left' ? '1em' : 'unset')};
    right: ${(props) => (props.$placement === 'left' ? '1em' : 'unset')};
    z-index: 1040;
    font-size: 1.6em;
    cursor: pointer;
`;

const NwDrawer = ({
    bgColor,
    children,
    fixed = false,
    fromLeft,
    fromTop,
    fullpage = false,
    icon,
    maxWidth = '100vw',
    minWidth = '0',
    modalDrawer,
    onClose,
    onTouchForm,
    padding,
    placement = 'right',
    title,
    touchForm,
    visible = true,
    whiteBg = true,
    width = '40rem',
    zIndex = 1040,
    ...props
}) => {
    const layoutContext = useLayoutContext();
    const { breakpoints, isMobile, forceCloseAllDrawers } = layoutContext;
    const dispatch = useLayoutContextDispatch();

    const [visibleDrawer, setVisibleDrawer] = useState(visible);

    // Sincronizza con il prop visible
    useEffect(() => {
        setVisibleDrawer(visible);
    }, [visible]);

    // Ascolta il contatore per chiudere tutti i drawer
    useEffect(() => {
        if (forceCloseAllDrawers > 0) {
            if (onClose) {
                onClose(); // Chiama onClose per informare il parent component
            } else {
                setVisibleDrawer(false);
            }
            // Reset del contatore dopo aver chiuso il drawer
            dispatch({ type: 'ResetForceCloseAllDrawers' });
        }
    }, [forceCloseAllDrawers, onClose, dispatch]);

    // Su mobile il padding di default si riduce per lasciare piu' spazio al contenuto.
    // Un padding passato esplicitamente dal chiamante viene sempre rispettato.
    const bodyPadding = padding !== undefined ? padding : isMobile ? '1rem' : '1.6rem';

    const drawerStyle = {};
    if (fromTop) {
        drawerStyle.marginTop = fromTop;
        drawerStyle.height = `calc(100dvh - ${fromTop}px)`;
    } else {
        if (isMobile) {
            drawerStyle.marginTop = '104px';
            drawerStyle.height = `calc(100dvh - 104px)`;
        }
    }
    if (fromLeft) {
        drawerStyle.marginLeft = fromLeft;
    }

    const handleClose = () => {
        if (touchForm) {
            confirm({
                title: (
                    <div>
                        You can lose your inputs
                        <br />
                        Are you sure to close the drawer?
                    </div>
                ),
                onOk() {
                    if (onTouchForm) {
                        onTouchForm(false);
                    }
                    onClose();
                },
                onCancel() {},
            });
        } else {
            onClose();
        }
    };

    return (
        <StyledDrawer
            $customBgColor={modalDrawer ? 'rgba(0, 0, 0, .65)' : bgColor}
            $minWidth={minWidth}
            $maxWidth={maxWidth}
            $padding={bodyPadding}
            $left={placement === 'left'}
            $fixed={fixed}
            $modalDrawer={modalDrawer}
            width={breakpoints === 1 ? '100vw' : fullpage || modalDrawer ? '90vw' : width}
            placement={placement}
            onClose={handleClose}
            destroyOnClose={true}
            open={visibleDrawer}
            zIndex={zIndex}
            title={title}
            style={drawerStyle}
            $whiteBg={modalDrawer ? false : whiteBg}
            closable={!fixed}
            {...props}
        >
            {!fixed && (
                <DrawerCloseButton $placement={placement}>
                    {modalDrawer ? (
                        <NwIcon icon={byPrefixAndName.fasl['times']} color="#fff" onClick={handleClose} />
                    ) : placement === 'left' ? (
                        <NwIcon icon={icon || byPrefixAndName.fasl['chevron-left']} onClick={handleClose} />
                    ) : (
                        <NwIcon icon={icon || byPrefixAndName.fasl['chevron-right']} onClick={handleClose} />
                    )}
                </DrawerCloseButton>
            )}
            {children}
        </StyledDrawer>
    );
};

export default NwDrawer;
